import React, { useState, useEffect, useCallback } from 'react';
import { StepProps } from '../MultiStepModal';
import { useToast } from '../../../contexts/ToastContext';
import { jobsApi } from '../../../utils/api';
import { UserIcon, CalendarDaysIcon, ClockIcon } from '@heroicons/react/24/outline';

interface Engineer {
  id: string;
  name: string;
  email: string;
}

export default function AssignEngineerStep({ data, onDataChange, onNext }: StepProps) {
  const { showToast } = useToast();
  const [engineers, setEngineers] = useState<Engineer[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchEngineers = useCallback(async () => {
    try {
      setLoading(true);
      const scheduledDate = data.scheduledDate || new Date().toISOString().split('T')[0];
      const response = await jobsApi.getAvailableEngineers(scheduledDate);
      setEngineers(response.data || []);
    } catch (error) {
      console.error('Error fetching engineers:', error);
      showToast('Failed to load engineers', 'error');
      setEngineers([]);
    } finally {
      setLoading(false);
    }
  }, [data.scheduledDate, showToast]);

  useEffect(() => {
    fetchEngineers();
  }, [fetchEngineers]);

  const handleInputChange = (field: string, value: string) => {
    onDataChange({ [field]: value });
    
    // Refresh engineers when date changes
    if (field === 'scheduledDate') {
      fetchEngineers();
    }
  };

  const handleEngineerSelect = (engineerId: string) => {
    const eng = engineers.find((e) => e.id === engineerId);
    onDataChange({ engineerId, engineerName: eng?.name || '' });
  };

  const validateForm = () => {
    if (!data.engineerId) {
      showToast('Please select an engineer', 'error');
      return false;
    }
    if (!data.scheduledDate) {
      showToast('Please select a scheduled date', 'error');
      return false;
    }
    if (!data.scheduledTime) {
      showToast('Please select a scheduled time', 'error');
      return false;
    }
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <div className="step-form">
      <div className="step-header">
        <h3 className="step-form-title">Assign Engineer</h3>
        <p className="step-form-subtitle">Select an engineer and schedule the job</p>
      </div>

      <form onSubmit={handleSubmit} className="step-form-content">
        <div className="form-group">
          <label className="form-label">Select Engineer *</label>
          {loading ? (
            <div className="loading-state">
              <p>Loading available engineers...</p>
            </div>
          ) : engineers.length > 0 ? (
            <div className="engineer-grid">
              {engineers.map((engineer) => (
                <div
                  key={engineer.id}
                  className={`engineer-card ${data.engineerId === engineer.id ? 'selected' : ''}`}
                  onClick={() => handleEngineerSelect(engineer.id)}
                >
                  <div className="engineer-avatar">
                    <UserIcon className="w-6 h-6" />
                  </div>
                  <div className="engineer-info">
                    <h4 className="engineer-name">{engineer.name}</h4>
                    <div className="engineer-meta">
                      <span className="engineer-status status-available">Available</span>
                      <span className="engineer-email">{engineer.email}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No engineers available for the selected date.</p>
              <p className="text-sm text-gray-500">Try selecting a different date.</p>
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <CalendarDaysIcon className="w-4 h-4 inline mr-2" />
              Scheduled Date *
            </label>
            <input
              type="date"
              className="form-input"
              value={data.scheduledDate || ''}
              onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              <ClockIcon className="w-4 h-4 inline mr-2" />
              Scheduled Time *
            </label>
            <select
              className="form-select"
              value={data.scheduledTime || ''}
              onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
              required
            >
              <option value="">Select time</option>
              <option value="08:00">08:00 AM</option>
              <option value="09:00">09:00 AM</option>
              <option value="10:00">10:00 AM</option>
              <option value="11:00">11:00 AM</option>
              <option value="12:00">12:00 PM</option>
              <option value="13:00">01:00 PM</option>
              <option value="14:00">02:00 PM</option>
              <option value="15:00">03:00 PM</option>
              <option value="16:00">04:00 PM</option>
              <option value="17:00">05:00 PM</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Special Instructions</label>
          <textarea
            className="form-textarea"
            placeholder="Any special instructions for the engineer..."
            value={data.instructions || ''}
            onChange={(e) => handleInputChange('instructions', e.target.value)}
            rows={2}
          />
        </div>
      </form>
    </div>
  );
}
